import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpPuebloClient } from '@http/http_pueblo_client';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { LoggingService } from '@services/logging.service';
import {
  PENDING_INTEREST_RESOURCE_NAME,
  makeInterestResourceName,
} from 'pueblo-types';
import {
  EMPTY,
  catchError,
  concatMap,
  map,
  mergeMap,
  of,
  tap,
  withLatestFrom,
} from 'rxjs';
import { selectAppUser } from '../user/user-selectors';
import * as actions from './interest-actions';
import {
  selectInterest,
  selectInterestsLiveReloading,
} from './interest-selectors';

@Injectable()
export class InterestEffects {
  private readonly actions$ = inject(Actions);
  private readonly puebloClient = inject(HttpPuebloClient);
  private readonly store = inject(Store);
  private readonly logger = inject(LoggingService);
  private readonly snackBar = inject(MatSnackBar);

  readonly fetchInterest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.fetchInterest),
      tap(({ resourceName }) => {
        this.logger.log3('[Effect] Fetching interest', resourceName);
      }),
      withLatestFrom(this.store.select(selectInterestsLiveReloading)),
      mergeMap(([{ resourceName }, interestsLiveReloading]) => {
        if (interestsLiveReloading.has(resourceName)) return EMPTY;
        interestsLiveReloading.add(resourceName);
        return this.puebloClient.getInterest(resourceName).pipe(
          map((interest) => actions.fetchInterestSuccess({ interest })),
          tap(() => {
            this.logger.log3('[Effect] Fetched interest', resourceName);
          }),
          catchError((message) => {
            this.logger.error(message);
            return of(actions.fetchInterestFail({ resourceName, message }));
          }),
        );
      }),
    );
  });

  readonly batchFetchInterests$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.batchFetchInterests),
      tap(({ resourceNames }) => {
        this.logger.log3(`[Effect] Batch Fetching interest`, resourceNames);
      }),
      withLatestFrom(this.store.select(selectInterestsLiveReloading)),
      mergeMap(([{ resourceNames }, interestsLiveReloading]) => {
        const fetchInterests = resourceNames.filter(
          (r) => !interestsLiveReloading.has(r),
        );
        if (!fetchInterests.length) return EMPTY;
        fetchInterests.forEach((h) => interestsLiveReloading.add(h));
        return this.puebloClient.batchGetInterests(fetchInterests).pipe(
          map((interests) => actions.batchFetchInterestsSuccess({ interests })),
          tap(() => {
            this.logger.log3('[Effect] Batch Fetched interests');
          }),
          catchError((message) => {
            this.logger.error(message);
            return of(actions.batchFetchInterestsFail({ message }));
          }),
        );
      }),
    );
  });

  readonly createInterest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.createInterest),
      tap(({ interest }) => {
        this.logger.log3(`[Effect] Create interest `, JSON.stringify(interest));
      }),
      map(({ interest, hooks }) => {
        let interestWithId = interest;
        if (interestWithId.resourceName === PENDING_INTEREST_RESOURCE_NAME) {
          interestWithId = {
            ...interest,
            resourceName: makeInterestResourceName(
              this.puebloClient.createInterestId(),
            ),
          };
        }
        hooks?.onPending?.(interestWithId);
        return actions.createInterestWithId({
          interest: interestWithId,
          hooks: hooks,
        });
      }),
    );
  });

  readonly createInterestWithId$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.createInterestWithId),
      tap(({ interest }) => {
        this.logger.log3(
          `[Effect] Create interest (with id) `,
          JSON.stringify(interest),
        );
      }),
      withLatestFrom(this.store.select(selectAppUser)),
      concatMap(([{ interest, hooks: callback }, appUser]) => {
        return this.puebloClient.createInterest(interest).pipe(
          tap((interest) => {
            this.logger.log3(
              `[Effect] Created interest `,
              JSON.stringify(interest),
            );
            callback?.onSuccess?.(interest);
          }),
          map((interest) => actions.createInterestSuccess({ interest })),
          catchError((message) => {
            this.logger.error(message);
            callback?.onFailure?.(message);
            return of(
              actions.createInterestFail({
                message,
                originalInterest: interest,
                originalAppUser: appUser,
              }),
            );
          }),
        );
      }),
    );
  });

  readonly updateInterest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.updateInterest),
      tap(({ interest }) => {
        this.logger.log3(`[Effect] Update interest`, JSON.stringify(interest));
      }),
      concatLatestFrom(({ interest }) =>
        this.store.select(selectInterest(interest.resourceName)),
      ),
      map(([{ interest, hooks }, originalInterest]) => {
        if (!originalInterest) {
          this.logger.error(
            `[Effect] Update interest failed: Interest not found`,
            JSON.stringify(interest),
          );
          return actions.updateInterestFail({
            message: 'Interest not found',
            originalInterest: undefined,
          });
        }
        hooks?.onPending?.(interest);
        return actions.updatePendingInterest({
          interest,
          originalInterest,
          hooks,
        });
      }),
    );
  });

  readonly updatePendingInterest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.updatePendingInterest),
      tap(({ interest }) => {
        this.logger.log3(
          `[Effect] Update interest (pending) `,
          JSON.stringify(interest),
        );
      }),
      concatMap(({ interest, originalInterest, hooks }) => {
        return this.puebloClient
          .updateInterest(interest, originalInterest)
          .pipe(
            map((interest) => actions.updateInterestSuccess({ interest })),
            tap(({ interest }) => {
              this.logger.log3(
                `[Effect] Updated interest (pending) `,
                JSON.stringify(interest),
              );
              hooks?.onSuccess?.(interest);
            }),
            catchError((message) => {
              this.logger.error(message);
              hooks?.onFailure?.(message);
              return of(
                actions.updateInterestFail({ message, originalInterest }),
              );
            }),
          );
      }),
    );
  });

  readonly deleteInterest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.deleteInterest),
      tap(({ resourceName }) => {
        this.logger.log3(`[Effect] Delete interest`, resourceName);
      }),
      concatLatestFrom(({ resourceName }) =>
        this.store.select(selectInterest(resourceName)),
      ),
      map(([{ resourceName, hooks }, originalInterest]) => {
        if (!originalInterest) {
          this.logger.error(
            `[Effect] Delete interest failed: Interest not found`,
            resourceName,
          );
          return actions.deleteInterestFail({
            message: 'Interest not found',
            originalInterest: undefined,
          });
        }
        hooks?.onPending?.(resourceName);
        return actions.deletePendingInterest({
          resourceName,
          originalInterest,
          hooks,
        });
      }),
    );
  });

  readonly deletePendingInterest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.deletePendingInterest),
      tap(({ resourceName }) => {
        this.logger.log3(`[Effect] Delete interest (pending) `, resourceName);
      }),
      concatMap(({ resourceName, originalInterest, hooks: callback }) => {
        return this.puebloClient.deleteInterest(resourceName).pipe(
          map(() => actions.deleteInterestSuccess({ resourceName })),
          tap(({ resourceName }) => {
            this.logger.log3(
              `[Effect] Deleted interest (pending) `,
              resourceName,
            );

            this.snackBar.open('Interest deleted successfully');
            callback?.onSuccess?.(resourceName);
          }),
          catchError((message) => {
            this.logger.error(message);
            callback?.onFailure?.(message);
            return of(
              actions.deleteInterestFail({ message, originalInterest }),
            );
          }),
        );
      }),
    );
  });

  readonly displayErrors$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          actions.fetchInterestFail,
          actions.batchFetchInterestsFail,
          actions.updateInterestFail,
          actions.createInterestFail,
          actions.deleteInterestFail,
        ),
        mergeMap(({ message }) => {
          // TODO: Return better/human-readable error messages.
          this.snackBar.open(message);
          return EMPTY;
        }),
      );
    },
    { dispatch: false },
  );
}
